import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/shared/hooks/use-auth'
import { getJwt } from '@/shared/lib/betterAuth/get-jwt'
import { DOM_ID } from '@/shared/constants/dom'
import { getKeycloakData } from '@/services/payment.services'
import {
  KEYCLOAK_STALE_TIME,
  KEYCLOAK_GC_TIME,
  SESSION_QUERY_KEY,
  PAYMENT_QUERY_KEYS,
} from '@/shared/constants/cache'
import { queryClient } from '@/shared/lib/tanstack'

function hasStoredToken(): boolean {
  return !!queryClient.getQueryData<{ user?: object }>(SESSION_QUERY_KEY)?.user
}

const CI_SNIPPET_URL = process.env.BUN_PUBLIC_CI_SNIPPET_URL ?? ''
const CI_ENVIRONMENT = process.env.BUN_PUBLIC_CI_ENVIRONMENT ?? ''
const CI_PARTNER = 'kanguro'
const CI_IFRAME_RESIZE_HEIGHT_OFFSET = 50
const CI_IFRAME_SCROLL_TOP_OFFSET = 100

let ciSnippetLoad: Promise<void> | undefined = undefined

function loadCiSnippet(): Promise<void> {
  if (ciSnippetLoad) return ciSnippetLoad
  if (window.ciIframeSnippet?.HandlerInstance) {
    ciSnippetLoad = Promise.resolve()
    return ciSnippetLoad
  }
  ciSnippetLoad = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = CI_SNIPPET_URL
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => {
      ciSnippetLoad = undefined
      reject(new Error(`Failed to load CI snippet: ${CI_SNIPPET_URL}`))
    }
    document.body.appendChild(script)
  })
  return ciSnippetLoad
}

function keycloakQueryOptions(enabled: boolean, product: string) {
  return {
    queryKey: PAYMENT_QUERY_KEYS.keycloak(product),
    queryFn: async () => {
      const jwt = await getJwt()
      return getKeycloakData(jwt)
    },
    enabled,
    staleTime: KEYCLOAK_STALE_TIME,
    gcTime: KEYCLOAK_GC_TIME,
  }
}

export function usePreloadPaymentIframe(
  product: string,
  enabled: boolean = true,
) {
  const keycloakQuery = useQuery(
    keycloakQueryOptions(hasStoredToken(), product),
  )

  useEffect(() => {
    if (!CI_SNIPPET_URL || !enabled) return
    loadCiSnippet()
  }, [enabled])

  useEffect(() => {
    const accessToken = keycloakQuery.data?.access_token
    if (!accessToken || !CI_SNIPPET_URL || !enabled) return

    const config = {
      partner: CI_PARTNER,
      product,
      environment: CI_ENVIRONMENT,
      customPayload: { accessToken },
      appendHostQueryParams: true,
      iframeSettings: {
        resizeHeightOffset: CI_IFRAME_RESIZE_HEIGHT_OFFSET,
        scrollTopOffset: CI_IFRAME_SCROLL_TOP_OFFSET,
      },
      containerId: DOM_ID.IFRAME_CONTAINER,
    }

    loadCiSnippet()
      .then(() => {
        window.ciIframeSnippet.HandlerInstance.init(config)
      })
      .catch((err) => {
        console.error('[usePaymentIframe]', err)
      })
  }, [keycloakQuery.data?.access_token, product])
}

export function usePaymentIframe(product: string) {
  const { user, loading } = useAuth()
  const keycloakQuery = useQuery(
    keycloakQueryOptions(hasStoredToken(), product),
  )

  return {
    user,
    loading,
    keycloakLoading: keycloakQuery.isLoading,
    keycloakError: keycloakQuery.error,
  }
}
